import React, { FC, useCallback, useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import useBrandContext from "Contexts/brand-context";
import Page from "Shared/components/page/page";
import Description from "Shared/components/description";
import Button from "Shared/components/button";
import InlineSvg from "Shared/components/inline-svg";
import TextLink from "Shared/components/text-link";
import { useTranslation } from "Shared/util/custom-hooks";
import {
    OpenBankingAccountsSidebar,
    OpenBankingAccountsSidebarProps,
} from "../../sidebars";
import { ConsentCards } from "./consent-cards";
import { usePayoutInformationContext } from "./context";
import { PayoutAccountDataTable } from "./payout-account-data-table";
import styles from "./payout-information-page.css";

type SidebarSettings = Pick<
    OpenBankingAccountsSidebarProps,
    "affiliatedBankId" | "bic" | "visible"
>;

export const PayoutInformationPage: FC = () => {
    const { pathname } = useLocation();
    const { push } = useHistory();
    const t = useTranslation((x) => x.company_settings.payoutInformationPage);
    const {
        brandData: { customerServiceEmailAddress, legalName: legalBrandName },
    } = useBrandContext();
    const {
        isLoading: isLoadingContext,
        loadingFailed: loadingContextFailed,
        reloadPayoutInformation,
        values: { accounts, bankConsents },
    } = usePayoutInformationContext();

    const [sidebarProps, setSidebarProps] = useState<SidebarSettings>({
        visible: false,
    });

    useEffect(() => {
        if (pathname === "/company-settings/payout-information") {
            setSidebarProps((prev) => ({ ...prev, visible: false }));
        } else if (
            pathname === "/company-settings/payout-information/add-account"
        ) {
            setSidebarProps((prev) => ({ ...prev, visible: true }));
        }
    }, [pathname]);

    const handleAddAccountClicked = () => {
        setSidebarProps((prev) => ({
            ...prev,
            affiliatedBankId: null,
            bic: null,
        }));
        push("/company-settings/payout-information/add-account");
    };

    const handleConsentCardClicked = useCallback(
        (bic: string, affiliatedBankId: string | null) => {
            setSidebarProps((prev) => ({ ...prev, affiliatedBankId, bic }));
            push("/company-settings/payout-information/add-account");
        },
        [push],
    );

    const handleSidebarCanceled = useCallback(() => {
        push("/company-settings/payout-information");
    }, [push]);

    const handleUserVerified = useCallback(() => {
        reloadPayoutInformation();
        handleSidebarCanceled();
    }, [handleSidebarCanceled, reloadPayoutInformation]);

    const handleEmailSupportClicked = () => {
        window.location.href = `mailto:${customerServiceEmailAddress}`;
    };

    const showEmptyState = !accounts?.length && !bankConsents?.length;
    const showConsentsSection = !!bankConsents?.length;

    return (
        <Page
            loading={isLoadingContext}
            returnLink={{
                path: "/company-settings",
                text: t.companySettings,
            }}
            title={{
                reducedMarginBottom: true,
                text: t.payoutInformation,
            }}
        >
            <div className={styles.descriptionContainer}>
                <h4>{t.descriptionTitle}</h4>
                <Description text={t.descriptionParagraphOne} />
                <Description
                    text={t.format(t.descriptionParagraphTwo, {
                        brandName: legalBrandName,
                    })}
                />
                <Description
                    text={t.format(t.descriptionParagraphThree, {
                        brandName: legalBrandName,
                    })}
                />
                <Description text={t.descriptionParagraphFour} />
                <Description text={t.descriptionParagraphFive} />
                <Description
                    text={
                        <>
                            {t.descriptionParagraphSix}{" "}
                            <TextLink
                                link={handleEmailSupportClicked}
                                text={customerServiceEmailAddress}
                            />
                        </>
                    }
                />
            </div>
            {showEmptyState ? (
                <Button
                    attributes={{ disabled: loadingContextFailed }}
                    buttonType={"action-primary"}
                    className={styles.addAccountButton}
                    onClick={handleAddAccountClicked}
                    text={t.addBankAccount}
                />
            ) : (
                <>
                    {showConsentsSection && (
                        <div className={styles.consentsContainer}>
                            <h4>{t.consents}</h4>
                            <ConsentCards
                                className={styles.consentCardsContainer}
                                onCardClick={handleConsentCardClicked}
                            />
                        </div>
                    )}
                    <div className={styles.payoutAccountDataTableContainer}>
                        <div className={styles.payoutAccountsHeaderContainer}>
                            <h4>{t.payoutAccounts}</h4>
                            <TextLink
                                className={styles.addTextLink}
                                link={handleAddAccountClicked}
                                text={
                                    <>
                                        <InlineSvg src="/add.svg" />
                                        {t.addBankAccount}
                                    </>
                                }
                            />
                        </div>
                        <PayoutAccountDataTable />
                    </div>
                </>
            )}
            <OpenBankingAccountsSidebar
                affiliatedBankId={sidebarProps.affiliatedBankId}
                bic={sidebarProps.bic}
                onCancel={handleSidebarCanceled}
                onUserVerify={handleUserVerified}
                visible={sidebarProps.visible}
            />
        </Page>
    );
};
